import { Component, Input, OnInit, inject } from '@angular/core';
import { ChangePasswordStatus } from '../../../core/enums/change-password-status.enum';
import { NgForm, FormsModule } from '@angular/forms';
import { LogoutReason } from '../../../core/enums/logout-reason.enum';
import { AuthenticationService, ChangePasswordResult } from '../../../core/services/authentication.service';
import { AuthPaths } from '../../enums/auth-paths';
import { NavigationService } from '../../../core/services/navigation.service';
import { PhoneRegExp } from '../../../shared/directives/phone-validator.directive';
import { supportEmailAddress } from '../../../core/constants';
import { NgClass } from '@angular/common';
import { MatProgressSpinner } from '@angular/material/progress-spinner';

export enum CreatePasswordMode {
  newUser,
  passwordReset,
  forcePasswordChange,
}

@Component({
    selector: 'app-create-password-form',
    templateUrl: './create-password-form.component.html',
    styleUrls: ['./create-password-form.component.scss'],
    imports: [FormsModule, NgClass, MatProgressSpinner]
})
export class CreatePasswordFormComponent implements OnInit {
  private authService = inject(AuthenticationService);
  private navigationService = inject(NavigationService);

  @Input() mode: CreatePasswordMode = null;

  createPasswordModes = CreatePasswordMode;
  supportEmailAddress = supportEmailAddress;

  formModel = { password: '', confirmPassword: '', phone: '', smsOptIn: false };
  submitted = false;
  submitting = false;
  loading = true;
  expired = false;
  phoneFieldDisabled = false;
  hideMfaFields = false;

  resetPasswordStatus: ChangePasswordStatus = null;
  resetPasswordStatuses = ChangePasswordStatus;
  phonePattern = PhoneRegExp;

  firstName = '';
  username = '';
  code = '';

  ngOnInit() {
    this.authService.logout(LogoutReason.creatingNewPassword);

    this.loading = true;
    this.expired = false;

    this.extractQueryParams();
    this.redirectOnMissingQueryParams();
    this.validateQueryParams();
  }

  onSubmit(createPasswordForm: NgForm) {
    this.submitted = true;

    if (createPasswordForm.valid) {
      this.submitting = true;

      this.authService
        .changePassword(
          this.username,
          this.code,
          this.formModel.password,
          this.formModel.confirmPassword,
          this.phoneFieldDisabled ? null : this.formModel.phone,
          this.formModel.smsOptIn
        )
        .subscribe((result: ChangePasswordResult) => {
          this.resetPasswordStatus = result.status;
          this.submitting = false;

          if (result.status === ChangePasswordStatus.MFARequired) {
            // @ts-ignore
            const { username, session, destination } = result.data;
            const params = new URLSearchParams({
              username,
              session,
              destination,
            });
            this.navigationService.goToUrl(`/${AuthPaths.confirmMfa}?${params.toString()}`);
          } else if (result.status === ChangePasswordStatus.Success) {
            this.navigationService.navigateHome();
          }
        });
    }
  }

  invalidCss(controlName: string, createPasswordForm: NgForm) {
    const control = createPasswordForm?.controls?.[controlName];
    if (!control) return {};
    const show = this.submitted || control.touched || control.dirty;
    return {
      'is-invalid': show && control.invalid
    };
  }

  private extractQueryParams() {
    const queryParams = this.navigationService.queryParams;

    // FIXME: our new email sending service encodes & in the query strings in urls to &amp; which is maybe incorrect
    // so we need to allow broken key names
    this.firstName = queryParams['first_name'] || queryParams["amp;first_name"];
    this.username = queryParams['username'] || queryParams["amp;username"];
    this.code = queryParams['code'] || queryParams["amp;code"];
  }

  private redirectOnMissingQueryParams() {
    if (!(this.firstName && this.username && this.code)) {
      this.navigationService.navigateHome();
    }
  }

  // Check to see if the params are valid before asking
  // the user for new password.
  private validateQueryParams() {
    this.authService.expiredResetPasswordParams(this.firstName, this.username, this.code).subscribe(result => {
      const hasPhone = result.obfuscatedPhone?.length > 0
      this.expired = result.expired;
      this.formModel.phone = result.obfuscatedPhone;
      this.hideMfaFields = hasPhone && result.smsOptedIn;
      this.phoneFieldDisabled = hasPhone;
      this.loading = false;
    });
  }
}
